import { useState } from 'react';
import { useTimer } from '@/shared/hooks/export';
import { useOpenWidget } from '@/core/widget-navigation/export';
import useSubscription from '@/core/messaging/hooks/use-subscription';
import messageService from '@/core/messaging/services/message-service';
import { MessageServiceConstants } from '@/core/messaging/model/message-service-constants';
import { MessageConnectionType } from '@/core/preferences/export';

const transports: Record<MessageConnectionType, { label: string; next?: string; url: string }> = {
  [MessageConnectionType.WEBSOCKET]: {
    label: 'Websockets',
    next: 'HTTP Streaming',
    url: messageService.brokerURL,
  },
  [MessageConnectionType.STREAMING]: {
    label: 'HTTP Streaming',
    next: 'HTTP Polling',
    url: messageService.fallbackURL,
  },
  [MessageConnectionType.POLLING]: {
    label: 'HTTP Polling',
    url: messageService.fallbackURL,
  },
};

/** How long the real-time connection may be down before the warning shows. */
const STALE_AFTER_MS = 30_000;

function isStale(): boolean {
  return Date.now() > messageService.lastSuccessfulConnectionTime().getTime() + STALE_AFTER_MS;
}

const settingsLinkClass = 'btn btn-link p-0 align-baseline';

export default function ConnectionWarning() {
  const openWidget = useOpenWidget();
  const openConnectionSettings = () => openWidget('settings', 'connection');
  // The staleness check runs inside the tick, not during render. useTimer no longer
  // re-renders its host, and while the socket is down the service returns the same Date
  // instance, so storing the date would never re-render once the 30 seconds had elapsed.
  const [stale, setStale] = useState(isStale);
  const [connectionType, setConnectionType] = useState<MessageConnectionType>(
    messageService.getConnectionType(),
  );

  const refresh = () => {
    setStale(isStale());
    setConnectionType(messageService.getConnectionType());
  };

  useTimer(5_000, refresh);
  useSubscription(MessageServiceConstants.MESSAGE_CONNECTION_STATE, refresh);

  if (stale) {
    const { label, next, url } = transports[connectionType];

    return (
      <div className={'alert alert-warning'}>
        <h5>Warning</h5>
        <p>
          You are not connected to our real-time services using <strong>{label}</strong>.{' '}
          {next ? (
            <>
              Try switching to <strong>{next}</strong> in our{' '}
              <button className={settingsLinkClass} onClick={openConnectionSettings}>
                settings menu
              </button>
              .
            </>
          ) : (
            <>
              Try{' '}
              <button className={settingsLinkClass} onClick={openConnectionSettings}>
                visiting our settings menu
              </button>{' '}
              for additional connectivity options.
            </>
          )}
        </p>
        <p>
          If you are behind a firewall, {label} connections will require that you can access the
          url: <code>{url}</code>
        </p>
      </div>
    );
  }

  return <></>;
}
